import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { setSkipModalData } from "../../redux/onboardingReducer";
import Loader from "../Loader";
import { getOnboardingDetails, onboardingQuestions, setDataForStates, setOnboardingData, createPlaidLinkToken, exchangePlaidPublicToken } from "../../shared/_services/common.services";
import { useSnackbar } from "notistack";
import Modal from "react-bootstrap/Modal";
import AIChatBot from "./AIChatBot";

import { usePlaidLink } from "react-plaid-link"; // Import Plaid hook
import PlaidFallbackModal from "../PlaidFallbackModal";

const QuestionFive = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [optionAmounts, setOptionAmounts] = useState({});
  const [showLoaderThree, setShowLoaderThree] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [isPlaidModalOpen, setIsPlaidModalOpen] = useState(false);
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [modalVariant, setModalVariant] = useState('success');
  const [modalMessage, setModalMessage] = useState('');
  const [modalSecondaryMessage, setModalSecondaryMessage] = useState('');
  // Plaid state
  const [linkToken, setLinkToken] = useState(null);
  const token = JSON.parse(localStorage.getItem("currentUser"))?.token;
  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  const payment_status = JSON.parse(localStorage.getItem("currentUser"))?.user?.payment_status;
  

  useEffect(() => {
    setIsPlaidModalOpen(true);
  }, []);


  const isFromUserQuestionnaire = location?.state?.fromUserQuestionnaire;
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const subscribed = !!currentUser?.user?.selected_plan;

  useEffect(() => {
    if (location?.pathname === "/confirm-payment" || subscribed) {
      setIsPlaidModalOpen(false);
    } else {
      setIsPlaidModalOpen(true);
    }
  }, [location]); // eslint-disable-line

  // Initialize Plaid Link Token
  useEffect(() => {
    if (!token || !userId) return;

    createPlaidLinkToken(userId, `${process.env.REACT_APP_URL}/q5`)
      .then((link_token) => setLinkToken(link_token))
      .catch((error) => {
        console.error("Error creating Plaid link token:", error);
        setModalVariant('error');
        setModalMessage('We couldn’t connect your accounts.');
        setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
        setShowPlaidModal(true);
      });
  }, [token, userId]);

  // Plaid Link configuration
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token) => {
      try {
        setShowLoaderThree(true);
        await exchangePlaidPublicToken(public_token, userId);
        setModalVariant('info');
        setModalMessage('Connecting your accounts…');
        setModalSecondaryMessage('This usually takes just a few seconds.\nYour information is encrypted and secure.');
        setShowPlaidModal(true);
        // You might want to refresh data or update state here

        // Step 4: Refresh onboarding data after successful Plaid connection
        await checkAndFillDataForQuesThree();
        // Update modal to success
        setModalVariant('success');
        setModalMessage('Your accounts are connected!');
        setModalSecondaryMessage('We pulled in the details we could. You can add or edit anything manually — estimates are totally fine.');

      } catch (error) {
        console.error("Error exchanging Plaid token:", error);
        setModalVariant('error');
        setModalMessage('We couldn’t connect your accounts.');
        setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
        setShowPlaidModal(true);
      } finally {
        setShowLoaderThree(false);
      }
    },
    onExit: (error) => {
      if (error) {
        console.error("Plaid link error:", error);
        setModalVariant('warning');
        setModalMessage('Your bank didn’t authorize the connection.');
        setModalSecondaryMessage('This is common if your bank requires extra verification. You can try again, or enter your info manually — your advisor can help.');
        setShowPlaidModal(true);
      }
    },
  });



  const checkAndFillDataForQuesThree = async () => {
    setShowLoaderThree(true);
    const questions = await onboardingQuestions("Debts");
    // #69: same crash class as QuestionSix — an API failure leaves `.data`
    // undefined, a malformed 200 body can leave it non-array-but-truthy, and
    // `allQuestions[0]` is read unguarded at render.
    setAllQuestions(Array.isArray(questions?.data) ? questions.data : []);
    if (questions.status === 200) {
      const getOnboardingDetailsData = await getOnboardingDetails(questions?.data[0]?._id);
      if (getOnboardingDetailsData.status === 200) {
        let onboardingData = null;
        if (Array.isArray(getOnboardingDetailsData?.data?.data)) {
          onboardingData = getOnboardingDetailsData?.data?.data[0];
        } else if (getOnboardingDetailsData?.data?.error) {
          onboardingData = [];
        }
        let temp = setDataForStates(questions.data[0], onboardingData, true);
        if (temp.selectedData.length > 0) {
          let data2 = {};
          let data1 = temp.selectedData.map((data) => {
            data2[data.name] = data.value;
            return data.name;
          });
          setSelectedOptions([...data1]);
          setOptionAmounts({ ...data2 });
        }
      }
    }
    setShowLoaderThree(false);
  };


  useEffect(() => {
    checkAndFillDataForQuesThree();
  }, []);



  const handleNextQuestion = async () => {
    setShowLoaderThree(true)
    // Check if any selected option has an empty or null value
    const hasInvalidValue = selectedOptions.some(option => !optionAmounts[option]);

    if (hasInvalidValue && subscribed) {
      enqueueSnackbar("Please enter a value for selected options or unselect", { variant: "error" });
      setShowLoaderThree(false);
      return;
    }
    let body = {};
    
    let currentUser = JSON.parse(localStorage.getItem('currentUser'));
    body.clientId = currentUser?.user?._id;
    body.questionId = allQuestions[0]?._id;
    body.questionName = allQuestions[0]?.questionName;
    let selectedData = selectedOptions.map((data) => ({
      "name": data,
      "value": optionAmounts[data] || ""
    }));
    let oldData = selectedOptions.map((data) => ({
      "name": data,
      "value": parseFloat(optionAmounts[data]) || 0
    }));
    let totalAssetsAmt = oldData.reduce((total, option) => total + option.value, 0);
    body.totalAssets = totalAssetsAmt;
    body = { ...body, selectedOptions: selectedData };
    if (isFromUserQuestionnaire) {
      let updateResponse = await setOnboardingData(body);
      setShowLoaderThree(false);
      
      if (updateResponse.statusText === 'Created') {
        navigate('/questionnaire');
      }
    } else {
      // For new entries, proceed to the next question
      if (selectedOptions.length > 0) {
        let data = await setOnboardingData(body);
        setShowLoaderThree(false)

        if (data.status === 201) {
          navigate('/q6');
        }
      } else {
        setShowLoaderThree(false)
        dispatch(setSkipModalData(true));
      }
    }
  };


  const toggleOption = (option) => {
    setSelectedOptions((prevSelectedOptions) => {
      if (prevSelectedOptions.includes(option)) {
        return prevSelectedOptions.filter((item) => item !== option);
      } else {
        return [...prevSelectedOptions, option];
      }
    });
  };

  const handleAmountChange = (option, amount) => {
    if (/^\d{0,10}(\.\d{0,2})?$/.test(amount)) {
      setOptionAmounts({ ...optionAmounts, [option]: amount });
    } else {
      enqueueSnackbar("Amount should not exceed 10 digits.", { variant: "error" });
    }
  };

  return (
    <>
      {showLoaderThree ? (
        <Loader />
      ) : (
        <div className="question-set">
          <div className="onboaring-quesn justify-content-between d-flex place-items-center">
            <h2>
              <span>Q5</span>
              <div>
                Want to give your advisor a quick snapshot of your money?
                <br />
                <p>Totally optional. Estimates are great. Your advisor can help fill this in later over chat.</p>
              </div>
            </h2>
            {!isFromUserQuestionnaire && (
              <button
                type="button"
                className="as-btn-secondary"
                onClick={() => { navigate('/dashboard'); }}
              >
                Continue Later
              </button>
            )}
          </div>

          {/* Add Plaid Connect Button */}
          <div className="mb-4 mt-3">
            <button
              className="as-btn-primary px-4 py-2"
              onClick={() => {
                // payment_status is a boolean on the backend (model/user.js);
                // this used to compare against the string "false", which a
                // boolean never equals, so a lapsed subscriber never got
                // routed to /package.
                if (!payment_status) {
                  navigate("/package", { state: { from: "/q5" } });
                } else {
                  open();
                }
              }}
              disabled={!ready || showLoaderThree}
            >
              <i className="fa fa-bank mr-2" aria-hidden="true"></i>
              {showLoaderThree ? "Connecting..." : "Connect Bank Account with Plaid"}
            </button>
            <p className="text-muted small mt-2">
              If you’re unsure, skip this step. Your advisor will walk through it with you.
            </p>
          </div>

          <div className="onboarding-label mb-4">
            <h5 className="mb-0">Add your accounts manually</h5>
            <p className="text-muted small">
              No need to be exact — estimates are perfectly fine. Your advisor will help you fill in the details.”
            </p>
          </div>

          <div className={`input-multi-select ${!subscribed ? "input-multi-select-disabled" : ""}`}>
            {allQuestions[0]?.options?.map((option) => (
              <div
                key={option}
                className={`input-multi-select-option ${selectedOptions.includes(option) ? "if_selected" : ""}`}
                onClick={() => toggleOption(option)}
              >
                <span>{option}</span>
                {subscribed && (
                  <input
                    placeholder="Balance Owed"
                    type="number"
                    value={optionAmounts[option] || ""}
                    onChange={(e) => handleAmountChange(option, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            ))}
          </div>
        </div>)}

      {!showLoaderThree && (
        <div className="onboarding-footer">
          <div className="onboarding-footer-btns">
            <div className="d-flex justify-content-between w-100">
              {isFromUserQuestionnaire ? (
                <>
                  <button
                    type="button"
                    className="as-btn-secondary"
                    onClick={() => {
                      navigate("/questionnaire")
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="as-btn-primary"
                    onClick={handleNextQuestion}
                  >
                    Update
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="as-btn-secondary"
                    onClick={() => navigate("/q4")}
                  >
                    ← Big Decisions
                  </button>
                  <button
                    type="button"
                    className="as-btn-primary"
                    onClick={handleNextQuestion}
                  >
                    Money Snapshot (Investments) →
                  </button>
                </>
              )}
            </div>
            <AIChatBot questionName={allQuestions[0]?.questionName}
              options={allQuestions[0]?.options} firstMsg="I can help you think through your debt situation. What types of debt are weighing on your mind, and how would you like to prioritize paying them off?" />
          </div>
        </div>
      )}


      {isPlaidModalOpen && (
        <Modal
          show={isPlaidModalOpen}
          aria-labelledby="documentPopupLabel"
          className="connect-plaid-modal"
          backdrop="static"
          keyboard={false}
          centered
          onHide={() => setIsPlaidModalOpen(false)}
        >
          <div className="modal-body p-0">
            <div className="plaid-modal-header">
              <h4 className="mb-2">Connect Your Financial Accounts with Plaid</h4>
              <div className="justify-content-between d-flex align-items-end gap-25">
                <p className="mb-0 text-12 f-300">
                  Do you want to connect your financial accounts via Plaid? This will automatically pull your debts and<br /> investments, keep your dashboard synced in real-time, and save you time entering data manually.
                </p>
                <i className="f-600 text-12 text-light ml-5">Note: Requires a subscribtion.</i>
              </div>

            </div>
            <div className="plaid-modal-content px-5">
              <div className="row">
                <div className="col-md-4 px-2 mb-3">
                  <div className="plaid-feature-box">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.25647 19.9375H15.7431C16.5939 19.9375 17.2706 19.2375 17.1791 18.431C16.5883 13.2344 13.0623 13.3203 13.0623 11C13.0623 8.67969 16.6334 8.80859 17.1787 3.56898C17.2646 2.76203 16.5939 2.0625 15.7431 2.0625H6.25647C5.40569 2.0625 4.73666 2.76203 4.82088 3.56898C5.36615 8.80859 8.93729 8.63672 8.93729 11C8.93729 13.3633 5.41127 13.2344 4.82088 18.431C4.72893 19.2375 5.40569 19.9375 6.25647 19.9375Z" stroke="url(#paint0_linear_1158_4356)" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14.7515 18.5625H7.26761C6.59729 18.5625 6.40823 17.7891 6.87831 17.3095C8.01612 16.1562 10.3128 15.3304 10.3128 14.0078V9.625C10.3128 8.77207 8.67999 8.12109 7.66979 6.7375C7.50307 6.50934 7.51983 6.1875 7.9435 6.1875H14.0764C14.4378 6.1875 14.516 6.50676 14.3514 6.73535C13.3558 8.12109 11.6878 8.76777 11.6878 9.625V14.0078C11.6878 15.3196 14.0816 16.0273 15.1425 17.3108C15.57 17.8282 15.4205 18.5625 14.7515 18.5625Z" fill="url(#paint1_linear_1158_4356)" />
                      <defs>
                        <linearGradient id="paint0_linear_1158_4356" x1="11" y1="2.0625" x2="11" y2="19.9375" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                        <linearGradient id="paint1_linear_1158_4356" x1="11.0006" y1="6.1875" x2="11.0006" y2="18.5625" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <h5>Save Time</h5>
                    <p>Instantly connect your accounts and skip manual entry forever.</p>
                  </div>
                </div>
                <div className="col-md-4 px-2 mb-3">
                  <div className="plaid-feature-box">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.2503 7.3335C17.9212 7.3335 17.1787 8.6535 17.4812 9.63433L14.227 12.8977C13.952 12.8152 13.5487 12.8152 13.2737 12.8977L10.9362 10.5602C11.2478 9.57933 10.5053 8.25016 9.16699 8.25016C7.83783 8.25016 7.08616 9.57016 7.39783 10.5602L3.21783 14.731C2.23699 14.4285 0.916992 15.171 0.916992 16.5002C0.916992 17.5085 1.74199 18.3335 2.75033 18.3335C4.07949 18.3335 4.82199 17.0135 4.51949 16.0327L8.69033 11.8527C8.96533 11.9352 9.36866 11.9352 9.64366 11.8527L11.9812 14.1902C11.6695 15.171 12.412 16.5002 13.7503 16.5002C15.0795 16.5002 15.8312 15.1802 15.5195 14.1902L18.7828 10.936C19.7637 11.2385 21.0837 10.496 21.0837 9.16683C21.0837 8.1585 20.2587 7.3335 19.2503 7.3335Z" fill="url(#paint0_linear_1158_4362)" />
                      <path d="M13.7503 8.25L14.612 6.3525L16.5003 5.5L14.612 4.6475L13.7503 2.75L12.907 4.6475L11.0003 5.5L12.907 6.3525L13.7503 8.25ZM3.20866 10.0833L3.66699 8.25L5.50033 7.79167L3.66699 7.33333L3.20866 5.5L2.75033 7.33333L0.916992 7.79167L2.75033 8.25L3.20866 10.0833Z" fill="url(#paint1_linear_1158_4362)" />
                      <defs>
                        <linearGradient id="paint0_linear_1158_4362" x1="11.0003" y1="7.3335" x2="11.0003" y2="18.3335" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                        <linearGradient id="paint1_linear_1158_4362" x1="8.70866" y1="2.75" x2="8.70866" y2="10.0833" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <h5>Full Insights</h5>
                    <p>Get accurate, real-time financial analytics tailored to your goals.</p>
                  </div>
                </div>
                <div className="col-md-4 px-2 mb-3">
                  <div className="plaid-feature-box">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.2503 7.3335C17.9212 7.3335 17.1787 8.6535 17.4812 9.63433L14.227 12.8977C13.952 12.8152 13.5487 12.8152 13.2737 12.8977L10.9362 10.5602C11.2478 9.57933 10.5053 8.25016 9.16699 8.25016C7.83783 8.25016 7.08616 9.57016 7.39783 10.5602L3.21783 14.731C2.23699 14.4285 0.916992 15.171 0.916992 16.5002C0.916992 17.5085 1.74199 18.3335 2.75033 18.3335C4.07949 18.3335 4.82199 17.0135 4.51949 16.0327L8.69033 11.8527C8.96533 11.9352 9.36866 11.9352 9.64366 11.8527L11.9812 14.1902C11.6695 15.171 12.412 16.5002 13.7503 16.5002C15.0795 16.5002 15.8312 15.1802 15.5195 14.1902L18.7828 10.936C19.7637 11.2385 21.0837 10.496 21.0837 9.16683C21.0837 8.1585 20.2587 7.3335 19.2503 7.3335Z" fill="url(#paint0_linear_1158_4362)" />
                      <path d="M13.7503 8.25L14.612 6.3525L16.5003 5.5L14.612 4.6475L13.7503 2.75L12.907 4.6475L11.0003 5.5L12.907 6.3525L13.7503 8.25ZM3.20866 10.0833L3.66699 8.25L5.50033 7.79167L3.66699 7.33333L3.20866 5.5L2.75033 7.33333L0.916992 7.79167L2.75033 8.25L3.20866 10.0833Z" fill="url(#paint1_linear_1158_4362)" />
                      <defs>
                        <linearGradient id="paint0_linear_1158_4362" x1="11.0003" y1="7.3335" x2="11.0003" y2="18.3335" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                        <linearGradient id="paint1_linear_1158_4362" x1="8.70866" y1="2.75" x2="8.70866" y2="10.0833" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <h5>No Limits</h5>
                    <p>Unlock all premium dashboard features and enjoy seamless
                      access.</p>
                  </div>
                </div>
                <div className="col-md-4 px-2 mb-3">
                  <div className="plaid-feature-box">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.66699 18.3332V16.4998H6.18783L5.82116 16.179C5.02671 15.4762 4.46908 14.6741 4.14824 13.7728C3.82741 12.8714 3.66699 11.9623 3.66699 11.0457C3.66699 9.34984 4.17483 7.841 5.19049 6.51917C6.20616 5.19734 7.53166 4.32284 9.16699 3.89567V5.82067C8.06699 6.21789 7.18088 6.89409 6.50866 7.84925C5.83644 8.80442 5.50033 9.86989 5.50033 11.0457C5.50033 11.7332 5.63019 12.4017 5.88991 13.0513C6.14963 13.7009 6.55449 14.3004 7.10449 14.8498L7.33366 15.079V12.8332H9.16699V18.3332H3.66699ZM12.8337 18.104V16.179C13.9337 15.7818 14.8198 15.1059 15.492 14.1513C16.1642 13.1968 16.5003 12.131 16.5003 10.954C16.5003 10.2665 16.3705 9.59825 16.1107 8.94925C15.851 8.30025 15.4462 7.70045 14.8962 7.14984L14.667 6.92067V9.1665H12.8337V3.6665H18.3337V5.49984H15.8128L16.1795 5.82067C16.9281 6.56928 17.4744 7.38298 17.8185 8.26175C18.1625 9.14053 18.3343 10.0379 18.3337 10.954C18.3337 12.6498 17.8258 14.1587 16.8102 15.4805C15.7945 16.8023 14.469 17.6768 12.8337 18.104Z" fill="url(#paint0_linear_1158_4403)" />
                      <defs>
                        <linearGradient id="paint0_linear_1158_4403" x1="11.0003" y1="3.6665" x2="11.0003" y2="18.3332" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <h5>Real-Time Sync</h5>
                    <p>Your debts, assets, and investments update daily without effort.
                    </p>
                  </div>
                </div>
                <div className="col-md-4 px-2 mb-3">
                  <div className="plaid-feature-box">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M10.083 1.8335C8.86743 1.8335 7.70164 2.31638 6.8421 3.17592C5.98256 4.03547 5.49967 5.20125 5.49967 6.41683C5.49967 7.63241 5.98256 8.79819 6.8421 9.65774C7.70164 10.5173 8.86743 11.0002 10.083 11.0002C11.2986 11.0002 12.4644 10.5173 13.3239 9.65774C14.1835 8.79819 14.6663 7.63241 14.6663 6.41683C14.6663 5.20125 14.1835 4.03547 13.3239 3.17592C12.4644 2.31638 11.2986 1.8335 10.083 1.8335ZM10.083 11.9168C7.88759 11.9168 5.88926 12.553 4.41984 13.4495C3.68651 13.8968 3.05951 14.4248 2.60667 14.9987C2.16117 15.5615 1.83301 16.2371 1.83301 16.9585C1.83301 17.7331 2.20976 18.3436 2.75242 18.779C3.26576 19.1915 3.94317 19.4647 4.66276 19.6553C6.10926 20.0376 8.03976 20.1668 10.083 20.1668C10.2945 20.1668 10.5038 20.1653 10.7109 20.1622C10.8644 20.1601 11.015 20.1195 11.1487 20.0441C11.2824 19.9686 11.3951 19.8608 11.4763 19.7305C11.5575 19.6002 11.6047 19.4516 11.6135 19.2983C11.6223 19.145 11.5925 18.992 11.5268 18.8532C11.1792 18.1175 10.9991 17.3139 10.9997 16.5002C10.9997 15.3525 11.3508 14.2892 11.9503 13.4082C12.0394 13.2773 12.093 13.1256 12.1061 12.9678C12.1191 12.8099 12.091 12.6514 12.0245 12.5077C11.958 12.364 11.8555 12.2399 11.7268 12.1476C11.5981 12.0553 11.4477 11.9979 11.2903 11.981C10.8955 11.9382 10.4931 11.9168 10.083 11.9168ZM17.2917 12.8307C17.211 12.6923 17.0954 12.5774 16.9564 12.4975C16.8174 12.4176 16.66 12.3756 16.4997 12.3756C16.3394 12.3756 16.1819 12.4176 16.0429 12.4975C15.904 12.5774 15.7884 12.6923 15.7077 12.8307L14.8735 14.2626L13.2547 14.6127C13.0979 14.6467 12.9528 14.7211 12.8339 14.8286C12.7149 14.9361 12.6262 15.073 12.5766 15.2255C12.5271 15.378 12.5184 15.5409 12.5515 15.6978C12.5846 15.8547 12.6583 16.0002 12.7652 16.1197L13.8688 17.3554L13.702 19.0036C13.6858 19.1631 13.7118 19.324 13.7773 19.4704C13.8427 19.6167 13.9454 19.7433 14.0751 19.8375C14.2048 19.9318 14.3569 19.9904 14.5163 20.0075C14.6757 20.0245 14.8368 19.9995 14.9835 19.9349L16.4997 19.2657L18.0158 19.9349C18.1626 19.9995 18.3236 20.0245 18.483 20.0075C18.6424 19.9904 18.7946 19.9318 18.9243 19.8375C19.0539 19.7433 19.1566 19.6167 19.2221 19.4704C19.2876 19.324 19.3135 19.1631 19.2973 19.0036L19.1305 17.3545L20.2342 16.1197C20.3411 16.0002 20.4147 15.8547 20.4478 15.6978C20.4809 15.5409 20.4723 15.378 20.4227 15.2255C20.3732 15.073 20.2845 14.9361 20.1655 14.8286C20.0465 14.7211 19.9014 14.6467 19.7447 14.6127L18.1258 14.2626L17.2917 12.8307Z" fill="url(#paint0_linear_1158_4376)" />
                      <defs>
                        <linearGradient id="paint0_linear_1158_4376" x1="11.1503" y1="1.8335" x2="11.1503" y2="20.1668" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <h5>Advisor Ready</h5>
                    <p>Advisor Ready Advisors always see up-to-date numbers for better guidance.</p>
                  </div>
                </div>
                <div className="col-md-4 px-2 mb-3">
                  <div className="plaid-feature-box">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.9998 0C17.5998 0 21.5804 7.3095 17.9985 12.8535L15.1248 17.303V19.25C15.1249 19.9309 14.8725 20.5876 14.4163 21.0931C13.9601 21.5985 13.3326 21.9168 12.6553 21.9862L12.3748 22H9.62476L9.34426 21.9862C8.66692 21.9168 8.03944 21.5985 7.58325 21.0931C7.12706 20.5876 6.87461 19.9309 6.87476 19.25V17.303L4.00101 12.8535C0.417757 7.30812 4.39976 0 10.9998 0ZM8.93726 19.25C8.93726 19.4323 9.00969 19.6072 9.13862 19.7361C9.26755 19.8651 9.44242 19.9375 9.62476 19.9375H12.3748C12.5571 19.9375 12.732 19.8651 12.8609 19.7361C12.9898 19.6072 13.0623 19.4323 13.0623 19.25V18.5625H8.93726V19.25ZM10.9998 2.0625C6.03326 2.0625 3.03851 7.5625 5.73351 11.7329L8.81076 16.5H13.1888L16.266 11.7329C18.961 7.5625 15.9663 2.0625 10.9998 2.0625ZM10.2229 5.3625C10.2801 5.2023 10.3855 5.06371 10.5246 4.96573C10.6637 4.86775 10.8296 4.81515 10.9998 4.81515C11.1699 4.81515 11.3358 4.86775 11.4749 4.96573C11.614 5.06371 11.7194 5.2023 11.7766 5.3625L12.3775 7.05787C12.4185 7.1736 12.4849 7.27872 12.5717 7.36554C12.6585 7.45237 12.7637 7.51872 12.8794 7.55975L14.5761 8.162C14.736 8.21919 14.8743 8.3244 14.9721 8.46323C15.0699 8.60205 15.1223 8.7677 15.1223 8.9375C15.1223 9.1073 15.0699 9.27295 14.9721 9.41177C14.8743 9.5506 14.736 9.65581 14.5761 9.713L12.8794 10.3152C12.7637 10.3563 12.6585 10.4226 12.5717 10.5095C12.4849 10.5963 12.4185 10.7014 12.3775 10.8171L11.7766 12.5139C11.7194 12.6741 11.614 12.8127 11.4749 12.9106C11.3358 13.0086 11.1699 13.0612 10.9998 13.0612C10.8296 13.0612 10.6637 13.0086 10.5246 12.9106C10.3855 12.8127 10.2801 12.6741 10.2229 12.5139L9.62201 10.8171C9.58097 10.7014 9.51462 10.5963 9.4278 10.5095C9.34097 10.4226 9.23586 10.3563 9.12013 10.3152L7.42476 9.713C7.26488 9.65581 7.12658 9.5506 7.0288 9.41177C6.93103 9.27295 6.87854 9.1073 6.87854 8.9375C6.87854 8.7677 6.93103 8.60205 7.0288 8.46323C7.12658 8.3244 7.26488 8.21919 7.42476 8.162L9.12013 7.55975C9.23586 7.51872 9.34097 7.45237 9.4278 7.36554C9.51462 7.27872 9.58097 7.1736 9.62201 7.05787L10.2229 5.3625Z" fill="url(#paint0_linear_1158_4383)" />
                      <defs>
                        <linearGradient id="paint0_linear_1158_4383" x1="10.9995" y1="0" x2="10.9995" y2="22" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#63363A" />
                          <stop offset="1" stopColor="#BA7293" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <h5>Smart Planning</h5>
                    <p>Track progress with insights based on verified, real data.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="plaid-modal-details-box mx-5">
              <h5>Your Security Is Our Priority</h5>
              <p>We use Plaid, a trusted financial technology company, to connect your accounts. Your data is encrypted end-to-end, and we never store your banking credentials. All information is protected with bank-level security measures.</p>
            </div>

            <div className="d-flex justify-content-between px-5 pb-4">
              <button type="button" className="as-btn-secondary text-12 py-2 px-4" onClick={() => navigate("/q4")} >
                Previous Question
              </button>
              <div className="d-flex gap-20">
                <button className="as-btn-secondary text-12 py-2 px-4" variant="secondary" onClick={() => setIsPlaidModalOpen(false)}>
                  No I'll Enter Manually
                </button>
                <button
                  type="button"
                  className="as-btn-primary text-12 py-2 px-4"
                  onClick={() => navigate('/package', { state: { from: "/q5" } })}>
                  Yes, Connect with Plaid
                </button>
              </div>

            </div>
          </div>
        </Modal>
      )}

      {showPlaidModal && (
        <PlaidFallbackModal variant={modalVariant} message={modalMessage} secondaryMessage={modalSecondaryMessage} onClose={() => setShowPlaidModal(false)} onTryAgain={open} />
      )}
    </>
  );
};

export default QuestionFive;
